/**
 * API keys: list, create (the plaintext key is shown exactly once, with a copy
 * button) and revoke.
 */
import { el, clear, toast } from '../../dom';
import { adminApi, type AdminApiKey } from '../../game-api';
import { fmtDateTime } from './format';
import { apiMessage, btn, confirmDialog, emptyBox, errorBox, input, labelled, modal, panel, pill, spinner, table, toastError,
} from './ui';

export function renderApiKeys(host: HTMLElement): () => void {
  let cancelled = false;
  const listHost = el('div', 'min-h-[60px]');

  async function load() {
    clear(listHost);
    listHost.appendChild(spinner());
    try {
      const keys = await adminApi.apiKeys.list();
      if (cancelled) return;
      clear(listHost);
      if (!keys?.length) { listHost.appendChild(emptyBox('No API keys yet.')); return; }
      listHost.appendChild(table(['Name', 'Prefix', 'Created', 'Last used', 'Status', ''], keys.map((k) => [
        k.name,
        el('span', 'font-mono text-xs text-text-dim', {}, `${k.prefix}…`),
        fmtDateTime(k.createdAt),
        fmtDateTime(k.lastUsedAt),
        k.revokedAt ? pill('revoked', 'bad') : pill('active', 'ok'),
        k.revokedAt ? el('span', 'hidden') : btn('Revoke', () => void revoke(k), 'danger'),
      ])));
    } catch (e) {
      if (cancelled) return;
      clear(listHost);
      listHost.appendChild(errorBox(apiMessage(e, "Couldn't load API keys."), () => void load()));
    }
  }

  async function revoke(k: AdminApiKey) {
    const ok = await confirmDialog('Revoke API key',
      `Revoke "${k.name}"? Anything using this key stops working immediately.`, 'Revoke');
    if (!ok) return;
    try {
      await adminApi.apiKeys.revoke(k.id);
      toast('Key revoked');
      void load();
    } catch (e) { toastError(e, 'Could not revoke the key'); }
  }

  function openCreate() {
    const name = input('e.g. Landing page');
    const create = btn('Create key', async () => {
      if (name.value.trim().length < 2) { toast('Give the key a name', 'error'); return; }
      try {
        const created = await adminApi.apiKeys.create(name.value.trim());
        close();
        // A response without `key` is NOT a success we can show: the plaintext is
        // only ever returned once, so there is nothing to copy and nothing to
        // recover. Say so and point at the fix (revoke + re-create) rather than
        // printing an empty box under "your new API key".
        if (created.key) showOnce(created.key);
        else toast('Key created, but the server did not return it — revoke it and create another', 'error');
        void load();
      } catch (e) { toastError(e, 'Could not create the key'); }
    }, 'primary');
    const close = modal('New API key',
      el('div', 'flex flex-col gap-3', {},
        labelled('Name', name),
        el('div', 'text-xs text-text-dim', {}, 'The key is shown once, right after it is created. Store it somewhere safe.')),
      [btn('Cancel', () => close()), create]);
  }

  function showOnce(key: string) {
    const field = el('div', 'font-mono text-xs break-all p-2.5 rounded-lg bg-surface-2 border border-border select-all', {}, key);
    const copy = btn('Copy', async () => {
      // Never claim a copy that did not happen: an empty write reads as "Key copied"
      // and the admin walks away from an unrecoverable secret.
      if (!key) { toast('Nothing to copy — the key was not returned', 'error'); return; }
      try {
        await navigator.clipboard.writeText(key);
        toast('Key copied');
      } catch {
        // Clipboard can be blocked (insecure context / permissions) — select it
        // so the admin can copy by hand instead of losing the key.
        const range = document.createRange();
        range.selectNodeContents(field);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        toast('Press Ctrl/Cmd+C to copy', 'error');
      }
    }, 'primary');
    const close = modal('Your new API key',
      el('div', 'flex flex-col gap-3', {},
        el('div', 'text-sm text-gold', {}, 'This is the only time the key is shown.'),
        field),
      [copy, btn('Done', () => close())]);
  }

  host.appendChild(panel('API keys',
    el('div', 'flex justify-end mb-3', {}, btn('Create key', openCreate, 'primary')),
    listHost));
  void load();
  return () => { cancelled = true; };
}
